import { useState, useEffect } from "react";
import { API_BASE_URL } from "../../config/api";
import { authHeader } from "../../utils/authHeader";

const EditProfileModal = ({ open, onClose, owner, refresh }) => {

  const [preview,    setPreview]    = useState(null);
  const [image,      setImage]      = useState(null);
  const [fullName,   setFullName]   = useState("");
  const [companyName,setCompanyName]= useState("");
  const [city,       setCity]       = useState("");
  const [state,      setState]      = useState("");
  const [district,   setDistrict]   = useState("");
  const [email,      setEmail]      = useState("");
  const [phone,      setPhone]      = useState("");
  const [gstin,      setGstin]      = useState("");
  const [saving,     setSaving]     = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [statesList,    setStatesList]    = useState([]);
  const [districtsList, setDistrictsList] = useState([]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res  = await fetch(`${API_BASE_URL}/api/location/states`);
        const data = await res.json();
        if (res.ok) setStatesList(data);
      } catch { }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    if (!state) return;
    const fetchDistricts = async () => {
      try {
        const res  = await fetch(`${API_BASE_URL}/api/location/districts/${state}`);
        const data = await res.json();
        if (res.ok) setDistrictsList(data);
      } catch { }
    };
    fetchDistricts();
  }, [state]);

  useEffect(() => {
    if (owner) {
      setFullName(owner.name        || "");
      setCompanyName(owner.companyName || "");
      setCity(owner.city            || "");
      setState(owner.state          || "");
      setDistrict(owner.district    || "");
      setEmail(owner.email          || "");
      setPhone(owner.phone          || "");
      setGstin(owner.gstin          || "");
      setPreview(null);
      setImage(null);
    }
  }, [owner]);

  if (!open) return null;

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    const formData = new FormData();
    formData.append("fullName",    fullName);
    formData.append("companyName", companyName);
    formData.append("city",        city);
    formData.append("state",       state);
    formData.append("district",    district);
    formData.append("phone",       phone);
    formData.append("gstin",       gstin.trim());

    // ← FIXED: was "profileImage", now "profilePhoto" to match backend
    if (image) formData.append("profilePhoto", image);

    try {
      const res  = await fetch(`${API_BASE_URL}/api/auth/update-profile`, {
        method:  "PUT",
        headers: { Authorization: authHeader().Authorization },
        body:    formData,
      });
      const data = await res.json();
      if (res.ok) {
        setSuccessMessage("Profile updated successfully ✓");
        refresh();
        setTimeout(() => { setSuccessMessage(""); onClose(); }, 1500);
      } else {
        alert(data.message || "Update failed");
      }
    } catch {
      alert("Profile update error");
    }
    setSaving(false);
  };

  // Get current profile photo — handle both Cloudinary URL and old local path
  const currentPhoto = owner?.profilePhoto
    ? owner.profilePhoto.startsWith("http")
      ? owner.profilePhoto                    // Cloudinary URL
      : `${API_BASE_URL}${owner.profilePhoto}` // old local path
    : "https://via.placeholder.com/80";

  return (
    <div className="modal-overlay">
      <div className="modal-card edit-profile-modal">

        <h3 style={{ textAlign: "center", marginBottom: 16, flexShrink: 0 }}>
          Edit Profile
        </h3>

        {successMessage && (
          <p style={{ color: "green", textAlign: "center", marginBottom: 10, flexShrink: 0 }}>
            {successMessage}
          </p>
        )}

        <div className="edit-form-scroll">

          {/* PROFILE IMAGE */}
          <div className="profile-upload">
            <div className="profile-upload-preview">
              <img
                src={preview || currentPhoto}  // ← FIXED: uses profilePhoto
                alt="Owner"
              />
            </div>
            <label className="upload-btn">
              Change Photo
              <input type="file" accept="image/*" hidden onChange={handleImageChange} />
            </label>
            <p className="upload-hint">Recommended size: 200 × 200 px (JPG / PNG)</p>
          </div>

          {/* FORM FIELDS */}
          <div className="edit-form">
            <input type="text" placeholder="Owner Name"
              value={fullName} onChange={e => setFullName(e.target.value)} />

            <input type="text" placeholder="Company Name"
              value={companyName} onChange={e => setCompanyName(e.target.value)} />

            <select value={state} onChange={e => { setState(e.target.value); setDistrict(""); }}>
              <option value="">Select State</option>
              {statesList.map((s, i) => <option key={i} value={s}>{s}</option>)}
            </select>

            <select value={district} onChange={e => setDistrict(e.target.value)} disabled={!state}>
              <option value="">Select District</option>
              {districtsList.map((d, i) => <option key={i} value={d}>{d}</option>)}
            </select>

            <input type="email" placeholder="Email Address" value={email} readOnly
              style={{ background: "#f4f7fe", color: "#a3aed0", cursor: "not-allowed" }} />

            <input type="text" placeholder="Phone Number"
              value={phone} onChange={e => setPhone(e.target.value)} />

            <input
              type="text"
              placeholder="GST Number (optional — e.g. 37XXXXX1234X1ZX)"
              value={gstin}
              onChange={e => setGstin(e.target.value.toUpperCase())}
              maxLength={15}
              style={{ letterSpacing: "0.5px" }}
            />
            <p style={{ fontSize: 11, color: "#9ca3af", marginTop: -10, marginBottom: 4 }}>
              Enter your GSTIN only if you are GST-registered. This will appear on your tax invoice for ITC claims.
            </p>
          </div>
        </div>

        {/* BUTTONS */}
        <div className="edit-actions">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button className="save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default EditProfileModal;